import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Search, Eye, BookOpen, Award, MessageSquare, Upload, ClipboardPaste, AlertTriangle, History, Users, UserCheck, Calendar, Video, MapPin, Shield, DollarSign, CreditCard, Star, Coins } from "lucide-react";
import { MultiSelect } from "@/components/ui/multi-select";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Kids belt ranks - v12.1.6: Added L0 Entry Level
const BELT_RANKS = [
  "L0 Entry Level 入級 (Jap Kup)",
  "White", "Yellow", "Orange", "Green", "Blue", "Purple", "Brown", 
  "Red", "Red 1 stripe", "Red 2 stripes", "Red 3 Stripes", "Black"
];

const CLASS_TYPES = [
  { value: "Tigers Kung Fu", label: "Tigers Kung Fu (Kids)", color: "bg-orange-500 text-white" },
  { value: "Dragons Kung Fu", label: "Dragons Kung Fu (Kids)", color: "bg-green-500 text-white" },
  { value: "Adults Kung Fu", label: "Adults Kung Fu", color: "bg-purple-500 text-white" },
  { value: "Tai Chi", label: "Tai Chi", color: "bg-teal-500 text-white" },
  { value: "Private Lessons", label: "Private Lessons", color: "bg-[#D4AF37] text-black" },
  { value: "Remote/International", label: "Remote/International", color: "bg-blue-600 text-white" },
];

// Group class types that appear on attendance sheets
const GROUP_CLASS_TYPES = ["Tigers Kung Fu", "Dragons Kung Fu", "Adults Kung Fu"];

// For multi-select dropdown options
const CLASS_TYPE_OPTIONS = CLASS_TYPES.map(c => ({
  value: c.value,
  label: c.label,
  color: c.color
}));

const GENDERS = ["Male", "Female", "Other"];

const DEFAULT_FEES = {
  "Tigers Kung Fu": 45,
  "Dragons Kung Fu": 45,
  "Adults Kung Fu": 55,
  "Tai Chi": 45,
  "Private Lessons": 60,
  "Remote/International": 50,
};

// Proficiency levels for form tracking - synced with Sifu Ledger
// v3.2: New 6-level progression with color gradient
const PROFICIENCY_LEVELS = [
  { value: "Learning", label: "Learning", color: "bg-gray-400 text-black" },
  { value: "Developing", label: "Developing", color: "bg-sky-400 text-black" },
  { value: "Proficient", label: "Proficient", color: "bg-green-500 text-white" },
  { value: "Refined", label: "Refined", color: "bg-orange-500 text-white" },
  { value: "Functional", label: "Functional", color: "bg-red-600 text-white" },
  { value: "Embodied", label: "Embodied", color: "bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] text-black font-bold" },
];

// ==============================================
// ADULT PILLAR OPTIONS (Adults Kung Fu only) — used for color mapping
// ==============================================
const ADULT_PILLARS = [
  { value: "Chou Kup 初級 – Beginner", label: "Chou Kup 初級 – Beginner", color: "bg-gray-500" },
  { value: "Jung Kup 中級 – Intermediate", label: "Jung Kup 中級 – Intermediate", color: "bg-blue-500" },
  { value: "Gou Kup 高級 – Advanced", label: "Gou Kup 高級 – Advanced", color: "bg-orange-500" },
  { value: "Gau Lin – Instructor", label: "Gau Lin – Instructor", color: "bg-purple-500" },
];

// ==============================================
// v11.9.6: UNIFIED HUNG KUEN LEVEL DROPDOWN
// Single dropdown replaces separate Pillar + Level selectors
// IDs 0-12 preserved for data integrity
// ==============================================
const UNIFIED_HK_LEVELS = [
  { id: 0, value: "0. Jap Kup — 入級 — Entry", pillar: "" },
  { id: 1, value: "1. Cho Kup Yat Kup — 初級一級", pillar: "Chou Kup 初級 – Beginner" },
  { id: 2, value: "2. Cho Kup Yee Kup — 初級二級", pillar: "Chou Kup 初級 – Beginner" },
  { id: 3, value: "3. Cho Kup Saam Kup — 初級三級", pillar: "Chou Kup 初級 – Beginner" },
  { id: 4, value: "4. Jung Kup Sei Kup — 中級四級", pillar: "Jung Kup 中級 – Intermediate" },
  { id: 5, value: "5. Jung Kup Ng Kup — 中級五級", pillar: "Jung Kup 中級 – Intermediate" },
  { id: 6, value: "6. Jung Kup Luk Kup — 中級六級", pillar: "Jung Kup 中級 – Intermediate" },
  { id: 7, value: "7. Gou Kup Chat Kup — 高級七級", pillar: "Gou Kup 高級 – Advanced" },
  { id: 8, value: "8. Gou Kup Baat Kup — 高級八級", pillar: "Gou Kup 高級 – Advanced" },
  { id: 9, value: "9. Gou Kup Gau Kup — 高級九級", pillar: "Gou Kup 高級 – Advanced" },
  { id: 10, value: "10. Gaau Lin Kap Saap Yat Kup — 教練級一級", pillar: "Gau Lin – Instructor" },
  { id: 11, value: "11. Gaau Lin Kap Saap Yee Kup — 教練級二級", pillar: "Gau Lin – Instructor" },
  { id: 12, value: "12. Gaau Lin Kap Saap Saam Kup — 教練級三級", pillar: "Gau Lin – Instructor" },
];

// Normalize old-format adult_level values to new unified format
const normalizeHkLevel = (oldValue) => {
  if (!oldValue) return "";
  if (UNIFIED_HK_LEVELS.find(l => l.value === oldValue)) return oldValue;
  const match = oldValue.match(/^(\d+)\./);
  if (match) {
    const found = UNIFIED_HK_LEVELS.find(l => l.id === parseInt(match[1], 10));
    if (found) return found.value;
  }
  return oldValue;
};

// Derive adult_pillar from unified level value
const derivePillarFromHkLevel = (levelValue) => {
  const level = UNIFIED_HK_LEVELS.find(l => l.value === levelValue);
  return level?.pillar || "";
};

// ==============================================
// TAI CHI 4-10-8 CURRICULUM LEVELS
// P1: Beginner (4 levels), P2: Intermediate (3 levels),
// P3: Advanced (2 levels), P4: Instructor (1 level)
// ==============================================
const TAI_CHI_LEVELS = [
  { value: "L0: Entry Gate 入門 (Jap Mun)", label: "L0: Entry Gate 入門 (Jap Mun)", phase: "Entry" },
  { value: "P1-L1: Wuji", label: "P1 Beginner — L1: Wuji (無極)", phase: "P1: Beginner" },
  { value: "P1-L2: Gate 1 Kin", label: "P1 Beginner — L2: Gate 1 ☰ Kin (Heaven)", phase: "P1: Beginner" },
  { value: "P1-L3: Gate 2 Dui", label: "P1 Beginner — L3: Gate 2 ☱ Dui (Lake)", phase: "P1: Beginner" },
  { value: "P1-L4: Gate 3 Lei", label: "P1 Beginner — L4: Gate 3 ☲ Lei (Fire)", phase: "P1: Beginner" },
  { value: "P2-L5: Gate 4 Jan", label: "P2 Intermediate — L5: Gate 4 ☳ Jan (Thunder)", phase: "P2: Intermediate" },
  { value: "P2-L6: Gate 5 Sun", label: "P2 Intermediate — L6: Gate 5 ☴ Sun (Wind)", phase: "P2: Intermediate" },
  { value: "P2-L7: Gate 6 Ham", label: "P2 Intermediate — L7: Gate 6 ☵ Ham (Water)", phase: "P2: Intermediate" },
  { value: "P3-L8: Gate 7 Gan", label: "P3 Advanced — L8: Gate 7 ☶ Gan (Mountain)", phase: "P3: Advanced" },
  { value: "P3-L9: Gate 8 Kwun", label: "P3 Advanced — L9: Gate 8 ☷ Kwun (Earth)", phase: "P3: Advanced" },
  { value: "P4-L10: Tai Gik Mun", label: "P4 Instructor — L10: Tai Gik Mun (太極門)", phase: "P4: Instructor" },
];

const getProficiencyColor = (proficiency) => {
  const prof = PROFICIENCY_LEVELS.find(p => p.value === proficiency);
  return prof?.color || "bg-gray-500";
};

const getAdultPillarColor = (pillar) => {
  const p = ADULT_PILLARS.find(x => x.value === pillar);
  return p?.color || "bg-gray-500";
};

// Helper: Check if student is in adult class (supports both single and multi-class)
const isAdultClass = (classTypeOrTypes) => {
  if (Array.isArray(classTypeOrTypes)) {
    return classTypeOrTypes.includes("Adults Kung Fu");
  }
  return classTypeOrTypes === "Adults Kung Fu";
};

// Helper: Check if student is in Tai Chi class
const isTaiChiClass = (classTypeOrTypes) => {
  if (Array.isArray(classTypeOrTypes)) {
    return classTypeOrTypes.includes("Tai Chi");
  }
  return classTypeOrTypes === "Tai Chi";
};

// Helper: Check if student is in a Kids class (Tigers or Dragons)
const isKidsClass = (classTypeOrTypes) => {
  if (Array.isArray(classTypeOrTypes)) {
    return classTypeOrTypes.some(c => c === "Tigers Kung Fu" || c === "Dragons Kung Fu");
  }
  return classTypeOrTypes === "Tigers Kung Fu" || classTypeOrTypes === "Dragons Kung Fu";
};

// Helper: Get all classes as array (handles both old and new format)
const getClassTypesArray = (student) => {
  if (student.class_types && student.class_types.length > 0) {
    return student.class_types;
  }
  if (student.class_type) {
    return [student.class_type];
  }
  return [];
};

const getBeltColor = (belt) => {
  const colors = {
    "L0 Entry Level 入級 (Jap Kup)": "bg-[#27272a] text-[#D4AF37] border border-[#D4AF37]/50",
    "White": "bg-gray-100 text-black",
    "Yellow": "bg-yellow-400 text-black",
    "Orange": "bg-orange-400 text-black",
    "Green": "bg-green-400 text-black",
    "Blue": "bg-blue-400 text-black",
    "Purple": "bg-purple-400 text-black",
    "Brown": "bg-amber-700 text-white",
    "Red": "bg-red-600 text-white",
    "Red 1 stripe": "bg-red-600 text-white",
    "Red 2 stripes": "bg-red-600 text-white",
    "Red 3 Stripes": "bg-red-600 text-white",
    "Black": "bg-gray-900 text-white border border-white/20",
  };
  return colors[belt] || "bg-gray-500 text-white";
};

const getClassColor = (classType) => {
  if (classType?.includes("Tigers")) return "class-tigers";
  if (classType?.includes("Dragons")) return "class-dragons";
  return "class-adults";
};

// Helper: Calculate age from date_of_birth (DD/MM/YYYY format)
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const parts = dateOfBirth.split('/');
  if (parts.length !== 3) return null;
  
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const year = parseInt(parts[2], 10);
  
  const birthDate = new Date(year, month, day);
  const today = new Date();
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // Adjust if birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

const emptyStudent = {
  name: "",
  gender: "",
  date_of_birth: "",
  join_date: "",
  contact: "",
  emergency_contact: "",
  email: "",
  class_type: "",  // Primary class (backward compat)
  class_types: [],  // NEW: Multiple classes
  belt_rank: "White",
  medical_notes: "",
  monthly_fee: 45,
  membership_start: "",
  membership_expiry: "",
  current_form_id: "",
  current_proficiency: "Novice",
  // Adult-only fields
  adult_pillar: "",
  adult_level: "",
  is_sifu: false,
  // Sifu notes - available for all students
  sifu_notes: "",
  // v10.7: Tai Chi Gate/Level
  tai_chi_level: "",
  // v3.4: Location/Timezone for International students
  location_timezone: "",
  // v3.7: Insurance/Membership tracking → v12.7.0: unified as "Membership & Insurance"
  membership_paid_date: "",
  // v7.0: Billing profile
  billing_type: "standard",
};

// v3.7: Payment categories for 4-stream financial system
const PAYMENT_CATEGORIES = [
  { value: "group_class", label: "Monthly Group Class", color: "bg-blue-500" },
  { value: "private", label: "Private Lesson", color: "bg-green-500" },
  { value: "grading", label: "Grading Fee", color: "bg-purple-500" },
  { value: "membership", label: "Membership & Insurance", color: "bg-orange-500" },
];

// v3.7: Insurance status helper
const getInsuranceStatus = (student) => {
  // Hide for Private Lessons and Remote/International
  if (student.class_type === "Private Lessons" || student.class_type === "Remote/International") {
    return null;
  }
  if (student.class_types?.includes("Private Lessons") || student.class_types?.includes("Remote/International")) {
    return null;
  }
  
  // Use membership_expiry directly if set, otherwise fall back to paid_date + 1 year
  let expiryDate = null;
  if (student.membership_expiry) {
    const raw = student.membership_expiry;
    if (raw.includes('/')) {
      const p = raw.split('/');
      expiryDate = new Date(`${p[2]}-${p[1]}-${p[0]}`);
    } else {
      expiryDate = new Date(raw);
    }
  } else if (student.membership_paid_date) {
    const paidDate = new Date(student.membership_paid_date);
    expiryDate = new Date(paidDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  }

  if (!expiryDate || isNaN(expiryDate)) {
    return { status: "none", label: "Not Recorded", color: "bg-gray-500", textColor: "text-gray-400" };
  }
  
  const today = new Date();
  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiry < 0) {
    return { status: "expired", label: "EXPIRED", color: "bg-red-500", textColor: "text-red-400", days: daysUntilExpiry, expiryDate };
  } else if (daysUntilExpiry <= 30) {
    return { status: "expiring", label: "Expiring Soon", color: "bg-orange-500", textColor: "text-orange-400", days: daysUntilExpiry, expiryDate };
  } else {
    return { status: "valid", label: "Valid", color: "bg-green-500", textColor: "text-green-400", days: daysUntilExpiry, expiryDate };
  }
};

export default function Students() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [forms, setForms] = useState([]);
  const [studentForms, setStudentForms] = useState([]); // Student form proficiency records
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterClass, setFilterClass] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState(emptyStudent);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form assignment dialog state
  const [showAddFormDialog, setShowAddFormDialog] = useState(false);
  const [selectedFormToAdd, setSelectedFormToAdd] = useState("");
  const [editingStudentForm, setEditingStudentForm] = useState(null);
  
  // Bulk Paste dialog state
  const [showBulkPasteDialog, setShowBulkPasteDialog] = useState(false);
  const [bulkPasteText, setBulkPasteText] = useState("");
  const [bulkPasteClasses, setBulkPasteClasses] = useState([]);
  const [bulkPasteBelt, setBulkPasteBelt] = useState("White");
  const [bulkPasteAdultPillar, setBulkPasteAdultPillar] = useState("");
  const [bulkPasteAdultLevel, setBulkPasteAdultLevel] = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);

  // Bulk selection state
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [singleDeleteStudent, setSingleDeleteStudent] = useState(null);

  // v12.6.0: Grading candidates tracking
  const [gradingCandidateIds, setGradingCandidateIds] = useState(new Set());
  const [markingCandidates, setMarkingCandidates] = useState(false);

  // v3.4: Training History state
  const [trainingHistory, setTrainingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [profileTab, setProfileTab] = useState("profile");

  // v12.5.0: Membership payment status from payments table
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loadingMembership, setLoadingMembership] = useState(false);

  // v3.7: Record Fee dialog state
  const [showRecordFeeDialog, setShowRecordFeeDialog] = useState(false);
  const [recordFeeData, setRecordFeeData] = useState({
    category: "group_class",
    amount: "",
    payment_method: "Cash",
    notes: "",
    date: new Date().toISOString().split('T')[0]
  });
  const [recordingFee, setRecordingFee] = useState(false);

  // v12.5.0: Record a fee payment — saves to unified payments table
  const handleRecordFee = async () => {
    if (!selectedStudent || !recordFeeData.amount) {
      toast.error("Please enter an amount");
      return;
    }
    
    setRecordingFee(true);
    try {
      // Map old category values to new unified category names
      const categoryMap = {
        "membership": "Membership",
        "grading": "Grading",
        "group_class": "Lesson",
        "private": "Lesson",
      };
      const category = categoryMap[recordFeeData.category] || "Lesson";
      
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      
      // Save to unified payments table
      await axios.post(`${API}/payments`, {
        student_id: selectedStudent.id,
        amount: parseFloat(recordFeeData.amount),
        month: month,
        year: year,
        status: "paid",
        payment_method: recordFeeData.payment_method,
        payment_date: recordFeeData.date,
        notes: recordFeeData.notes,
        category: category,
      });
      
      // If membership, also update the student's membership_paid_date for backward compat
      if (recordFeeData.category === "membership") {
        await axios.put(`${API}/students/${selectedStudent.id}`, {
          membership_paid_date: recordFeeData.date
        });
      }
      
      toast.success("Fee recorded successfully");
      setShowRecordFeeDialog(false);
      setRecordFeeData({
        category: "group_class",
        amount: "",
        payment_method: "Cash",
        notes: "",
        date: new Date().toISOString().split('T')[0]
      });
      fetchStudents();
      // Refresh membership status if viewing profile
      if (selectedStudent) {
        fetchMembershipStatus(selectedStudent.id);
      }
    } catch (error) {
      toast.error("Failed to record fee");
      console.error(error);
    } finally {
      setRecordingFee(false);
    }
  };

  // v3.4: Fetch unified training history (Group + Private sessions)
  const fetchTrainingHistory = async (studentId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/students/${studentId}/training-history`);
      setTrainingHistory(response.data || []);
    } catch (error) {
      console.error("Failed to fetch training history:", error);
      // Fallback: fetch separately and merge
      try {
        const [attendanceRes, privateRes] = await Promise.all([
          axios.get(`${API}/attendance?student_id=${studentId}`),
          axios.get(`${API}/private-sessions?student_id=${studentId}`)
        ]);
        
        const groupSessions = (attendanceRes.data || []).map(a => ({
          id: a.id,
          date: a.date,
          type: "Group",
          class_type: a.class_type || "Group Class",
          status: a.status || "present",
          notes: null,
          time: a.check_in_time
        }));
        
        const privateSessions = (privateRes.data || []).map(p => ({
          id: p.id,
          date: p.session_date,
          type: p.type === "Online" ? "Private (Online)" : "Private (F2F)",
          class_type: "Private Lesson",
          status: p.status,
          notes: p.sifu_notes,
          time: p.start_time,
          payment_status: p.payment_status
        }));
        
        // Merge and sort by date descending
        const merged = [...groupSessions, ...privateSessions].sort((a, b) => {
          const dateA = a.date.includes('/') ? 
            new Date(a.date.split('/').reverse().join('-')) : new Date(a.date);
          const dateB = b.date.includes('/') ? 
            new Date(b.date.split('/').reverse().join('-')) : new Date(b.date);
          return dateB - dateA;
        });
        
        setTrainingHistory(merged);
      } catch (fallbackError) {
        console.error("Fallback fetch also failed:", fallbackError);
        setTrainingHistory([]);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchForms();
    fetchStudentForms();
    fetchGradingCandidates();
    
    // Auto-open add dialog if ?action=add is in URL
    if (searchParams.get("action") === "add") {
      setFormData(emptyStudent);
      setIsEditing(false);
      setDialogOpen(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(response.data);
    } catch (error) {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/forms`);
      setForms(response.data);
    } catch (error) {
      console.error("Failed to load forms");
    }
  };

  const fetchStudentForms = async () => {
    try {
      const response = await axios.get(`${API}/student-forms`);
      setStudentForms(response.data);
    } catch (error) {
      console.error("Failed to load student forms");
    }
  };

  // v12.6.0: Fetch students who have active (unpaid) Grading payment records
  const fetchGradingCandidates = async () => {
    try {
      const res = await axios.get(`${API}/payments`);
      const payments = res.data || [];
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      // Find students with unpaid Grading records in current period (or any recent period)
      const candidateIds = new Set();
      payments.forEach(p => {
        if (p.category === 'Grading' && p.status !== 'paid') {
          candidateIds.add(p.student_id);
        }
      });
      setGradingCandidateIds(candidateIds);
    } catch (error) {
      console.error("Failed to fetch grading candidates:", error);
    }
  };

  // v12.6.0: Mark selected students as Grading Candidates
  const handleMarkGradingCandidates = async () => {
    if (selectedStudentIds.size === 0) return;
    setMarkingCandidates(true);
    try {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      let created = 0;
      let skipped = 0;
      
      for (const studentId of selectedStudentIds) {
        try {
          await axios.post(`${API}/payments`, {
            student_id: studentId,
            amount: 0,
            month: month,
            year: year,
            status: "unpaid",
            category: "Grading",
            notes: "Grading candidate — auto-tagged",
          });
          created++;
        } catch (err) {
          // If duplicate exists, it'll be updated — count as skipped
          skipped++;
        }
      }
      
      if (created > 0) {
        toast.success(`Tagged ${created} student${created !== 1 ? 's' : ''} as Grading Candidates`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} already tagged — records updated`);
      }
      
      setSelectedStudentIds(new Set());
      fetchGradingCandidates();
    } catch (error) {
      toast.error("Failed to tag grading candidates");
      console.error(error);
    } finally {
      setMarkingCandidates(false);
    }
  };

  // Bulk Import Handler
  const handleBulkImport = async () => {
    if (!bulkPasteText.trim()) {
      toast.error("Please paste student names");
      return;
    }
    if (bulkPasteClasses.length === 0) {
      toast.error("Please select at least one class");
      return;
    }

    setBulkImporting(true);
    
    // Parse names - one per line, skip empty lines
    const names = bulkPasteText
      .split('\n')
      .map(n => n.trim())
      .filter(n => n.length > 0);
    
    if (names.length === 0) {
      toast.error("No valid names found");
      setBulkImporting(false);
      return;
    }

    const isAdult = bulkPasteClasses.includes("Adults Kung Fu");

    try {
      const response = await axios.post(`${API}/students/bulk-import`, {
        students: names.map(name => ({
          name,
          class_types: bulkPasteClasses,
          belt_rank: isAdult ? "White" : bulkPasteBelt,
          adult_pillar: isAdult ? bulkPasteAdultPillar || null : null,
          adult_level: isAdult ? bulkPasteAdultLevel || null : null,
        })),
        default_gender: "Other",
        default_contact: "TBC",
        default_emergency_contact: "TBC",
        default_dob: "01/01/2000"
      });

      if (response.data.created_count > 0) {
        toast.success(`Successfully imported ${response.data.created_count} students!`);
        fetchStudents();
        setShowBulkPasteDialog(false);
        setBulkPasteText("");
        setBulkPasteClasses([]);
        setBulkPasteBelt("White");
        setBulkPasteAdultPillar("");
        setBulkPasteAdultLevel("");
      }
      
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} students failed to import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import students");
    } finally {
      setBulkImporting(false);
    }
  };

  // Bulk selection handlers
  const handleSelectStudent = (studentId, checked) => {
    const newSelected = new Set(selectedStudentIds);
    if (checked) {
      newSelected.add(studentId);
    } else {
      newSelected.delete(studentId);
    }
    setSelectedStudentIds(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(filteredStudents.map(s => s.id));
      setSelectedStudentIds(allIds);
    } else {
      setSelectedStudentIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStudentIds.size === 0) return;
    
    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/students/bulk-delete`, 
        Array.from(selectedStudentIds)
      );
      
      if (response.data.deleted_count > 0) {
        toast.success(`Successfully deleted ${response.data.deleted_count} student(s)`);
        fetchStudents();
        setSelectedStudentIds(new Set());
      }
      
      if (response.data.error_count > 0) {
        toast.error(`Failed to delete ${response.data.error_count} student(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete students");
    } finally {
      setBulkDeleting(false);
      setShowDeleteConfirmDialog(false);
    }
  };

  const getFormById = (formId) => {
    return forms.find(f => f.id === formId);
  };

  // Get forms assigned to a specific student (from student_form_proficiency table)
  const getStudentAssignedForms = (studentId) => {
    return studentForms.filter(sf => sf.student_id === studentId);
  };

  // Add form to student via student_form_proficiency table
  const handleAddFormToStudent = async () => {
    if (!selectedStudent || !selectedFormToAdd) return;
    
    try {
      const response = await axios.post(`${API}/student-forms`, {
        student_id: selectedStudent.id,
        form_id: selectedFormToAdd,
        proficiency: "Introduced",
        notes: ""
      });
      
      setStudentForms(prev => [...prev, response.data]);
      toast.success("Form assigned to student");
      setShowAddFormDialog(false);
      setSelectedFormToAdd("");
    } catch (error) {
      if (error.response?.data?.detail?.includes("already")) {
        toast.error("Form already assigned to this student");
      } else {
        toast.error("Failed to assign form");
      }
    }
  };

  // Update form proficiency and notes
  const handleUpdateStudentForm = async (studentFormId, updates) => {
    try {
      await axios.put(`${API}/student-forms/${studentFormId}`, updates);
      setStudentForms(prev => prev.map(sf => 
        sf.id === studentFormId ? { ...sf, ...updates } : sf
      ));
      toast.success("Form updated");
    } catch (error) {
      toast.error("Failed to update form");
    }
  };

  // Remove form from student
  const handleRemoveFormFromStudent = async (studentFormId) => {
    if (!confirm("Remove this form from the student?")) return;
    
    try {
      await axios.delete(`${API}/student-forms/${studentFormId}`);
      setStudentForms(prev => prev.filter(sf => sf.id !== studentFormId));
      toast.success("Form removed");
    } catch (error) {
      toast.error("Failed to remove form");
    }
  };

  // Get available forms (not yet assigned to student)
  const getAvailableForms = (studentId) => {
    const assignedFormIds = getStudentAssignedForms(studentId).map(sf => sf.form_id);
    return forms.filter(f => !assignedFormIds.includes(f.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields - use class_types array instead of single class_type
    const classesToCheck = formData.class_types && formData.class_types.length > 0 
      ? formData.class_types 
      : (formData.class_type ? [formData.class_type] : []);
    
    if (!formData.name || !formData.contact || classesToCheck.length === 0 || !formData.gender || !formData.date_of_birth) {
      toast.error("Please fill in all required fields (including at least one class)");
      return;
    }

    try {
      const hasAdultClass = isAdultClass(classesToCheck);
      const hasPrivateOrInternational = classesToCheck.some(c => c === "Private Lessons" || c === "Remote/International");
      
      // Prepare data - clear adult fields if not adult class
      const dataToSend = {
        ...formData,
        class_types: classesToCheck,
        class_type: classesToCheck[0] || "",  // Primary class = first selected
        join_date: formData.join_date || null,
        current_form_id: formData.current_form_id || null,
        current_proficiency: formData.current_proficiency || "Novice",
        // Only include adult fields if adult class
        adult_pillar: hasAdultClass ? derivePillarFromHkLevel(formData.adult_level) : null,
        adult_level: hasAdultClass ? (formData.adult_level || null) : null,
        is_sifu: hasAdultClass ? (formData.is_sifu || false) : false,
        // v10.7: Only include tai chi level if Tai Chi class
        tai_chi_level: classesToCheck.includes("Tai Chi") ? (formData.tai_chi_level || null) : null,
        // Sifu notes available for all students
        sifu_notes: formData.sifu_notes || "",
        billing_type: formData.billing_type || "standard",
        // v3.4: Location/Timezone for Private Only & International
        location_timezone: hasPrivateOrInternational ? (formData.location_timezone || "") : "",
      };

      if (isEditing && selectedStudent) {
        await axios.put(`${API}/students/${selectedStudent.id}`, dataToSend);
        toast.success("Student updated successfully");
      } else {
        await axios.post(`${API}/students`, dataToSend);
        toast.success("Student added successfully");
      }
      setDialogOpen(false);
      setFormData(emptyStudent);
      setIsEditing(false);
      setSelectedStudent(null);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save student");
    }
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setFormData({
      name: student.name,
      gender: student.gender,
      date_of_birth: student.date_of_birth,
      join_date: student.join_date || "",
      contact: student.contact,
      emergency_contact: student.emergency_contact,
      class_type: student.class_type,
      class_types: getClassTypesArray(student),  // Use helper to get array
      belt_rank: student.belt_rank,
      medical_notes: student.medical_notes || "",
      monthly_fee: student.monthly_fee,
      current_form_id: student.current_form_id || "",
      current_proficiency: student.current_proficiency || "Novice",
      // Adult fields
      adult_pillar: student.adult_pillar || "",
      adult_level: normalizeHkLevel(student.adult_level || ""),
      is_sifu: student.is_sifu || false,
      // v10.7: Tai Chi level
      tai_chi_level: student.tai_chi_level || "",
      // Sifu notes
      sifu_notes: student.sifu_notes || "",
      // v3.4: Location/Timezone
      location_timezone: student.location_timezone || "",
      // v3.7: Membership paid date
      membership_paid_date: student.membership_paid_date || "",
      // v7.0: Billing profile
      billing_type: student.billing_type || "standard",
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDelete = async (studentId, studentName) => {
    // Set the student for single delete confirmation
    setSingleDeleteStudent({ id: studentId, name: studentName });
  };

  const confirmSingleDelete = async () => {
    if (!singleDeleteStudent) return;
    try {
      await axios.delete(`${API}/students/${singleDeleteStudent.id}`);
      toast.success("Student deleted");
      fetchStudents();
      setSingleDeleteStudent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete student");
    }
  };

  // v12.8.0: Fetch membership payment status from BOTH payments + legacy fee_payments tables
  const fetchMembershipStatus = async (studentId) => {
    setLoadingMembership(true);
    try {
      const res = await axios.get(`${API}/payments/unified?student_id=${studentId}`);
      const payments = res.data || [];
      // Find the most recent paid Membership payment (from either table)
      const membershipPayments = payments.filter(
        p => (p.category === "Membership") && (p.status === "paid" || p._legacy)
      );
      if (membershipPayments.length > 0) {
        // Sort by payment_date descending
        membershipPayments.sort((a, b) => {
          const da = a.payment_date || a.date ? new Date(a.payment_date || a.date) : new Date(a.created_at || 0);
          const db = b.payment_date || b.date ? new Date(b.payment_date || b.date) : new Date(b.created_at || 0);
          return db - da;
        });
        const latest = membershipPayments[0];
        const paidDate = latest.payment_date || latest.date ? new Date(latest.payment_date || latest.date) : new Date(latest.created_at);
        const expiryDate = new Date(paidDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        
        if (daysUntilExpiry < 0) {
          setMembershipStatus({ status: "expired", label: "EXPIRED", color: "bg-red-500", textColor: "text-red-400", days: daysUntilExpiry, expiryDate, paidDate });
        } else if (daysUntilExpiry <= 30) {
          setMembershipStatus({ status: "expiring", label: "Expiring Soon", color: "bg-orange-500", textColor: "text-orange-400", days: daysUntilExpiry, expiryDate, paidDate });
        } else {
          setMembershipStatus({ status: "valid", label: "Valid", color: "bg-green-500", textColor: "text-green-400", days: daysUntilExpiry, expiryDate, paidDate });
        }
      } else {
        // Check for unpaid/pending Membership records
        const pendingMembership = payments.filter(p => p.category === "Membership" && p.status !== "paid");
        if (pendingMembership.length > 0) {
          setMembershipStatus({ status: "due", label: "Due", color: "bg-yellow-500", textColor: "text-yellow-400", days: null, expiryDate: null, paidDate: null });
        } else {
          setMembershipStatus({ status: "none", label: "Not Recorded", color: "bg-gray-500", textColor: "text-gray-400", days: null, expiryDate: null, paidDate: null });
        }
      }
    } catch (error) {
      console.error("Failed to fetch membership status:", error);
      // Fallback to old static method
      setMembershipStatus(null);
    } finally {
      setLoadingMembership(false);
    }
  };

  const handleView = (student) => {
    setSelectedStudent(student);
    setProfileTab("profile");
    setTrainingHistory([]);
    setMembershipStatus(null);
    setViewDialogOpen(true);
    fetchTrainingHistory(student.id);
    fetchMembershipStatus(student.id);
  };

  // Quick update proficiency from profile view
  const handleQuickUpdateProficiency = async (newProficiency) => {
    if (!selectedStudent) return;
    try {
      await axios.put(`${API}/students/${selectedStudent.id}`, { current_proficiency: newProficiency });
      toast.success("Proficiency updated");
      setSelectedStudent({ ...selectedStudent, current_proficiency: newProficiency });
      fetchStudents();
    } catch (error) {
      toast.error("Failed to update proficiency");
    }
  };

  // Quick update current form from profile view
  const handleQuickUpdateCurrentForm = async (formId) => {
    if (!selectedStudent) return;
    try {
      await axios.put(`${API}/students/${selectedStudent.id}`, {
        current_form_id: formId || null,
        current_proficiency: "Novice"
      });
      toast.success("Current form updated");
      setSelectedStudent({ ...selectedStudent, current_form_id: formId, current_proficiency: "Novice" });
      fetchStudents();
    } catch (error) {
      toast.error("Failed to update current form");
    }
  };

  // ==============================================
  // Quick update adult pillar (Adults only)
  // ==============================================
  const handleQuickUpdateAdultPillar = async (newPillar) => {
    if (!selectedStudent || !isAdultClass(selectedStudent.class_type)) return;
    try {
      await axios.put(`${API}/students/${selectedStudent.id}`, { adult_pillar: newPillar || null });
      toast.success("Adult pillar updated");
      setSelectedStudent({ ...selectedStudent, adult_pillar: newPillar });
      fetchStudents();
    } catch (error) {
      toast.error("Failed to update adult pillar");
    }
  };

  // ==============================================
  // Quick update adult level (Adults only)
  // Level 12 does NOT auto-grant Sifu status
  // Sifu must be granted separately via dedicated endpoint
  // ==============================================
  const handleQuickUpdateAdultLevel = async (newLevel) => {
    if (!selectedStudent || !isAdultClass(selectedStudent.class_type)) return;
    try {
      await axios.put(`${API}/students/${selectedStudent.id}`, { 
        adult_level: newLevel || null
      });
      toast.success("Adult level updated");
      setSelectedStudent({ ...selectedStudent, adult_level: newLevel });
      fetchStudents();
    } catch (error) {
      toast.error("Failed to update adult level");
    }
  };

  // ==============================================
  // Grant Sifu status (Adults only) - Uses dedicated endpoint
  // Sifu is a distinct rank that must be manually granted
  // ==============================================
  const handleGrantSifu = async () => {
    if (!selectedStudent || !isAdultClass(getClassTypesArray(selectedStudent))) return;
    try {
      const response = await axios.post(`${API}/students/${selectedStudent.id}/grant-sifu`, {
        granted_by: "Admin"
      });
      toast.success("Sifu status granted!");
      setSelectedStudent(response.data.student);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant Sifu status");
    }
  };

  // ==============================================
  // Revoke Sifu status (Adults only) - Uses dedicated endpoint
  // ==============================================
  const handleRevokeSifu = async () => {
    if (!selectedStudent || !isAdultClass(getClassTypesArray(selectedStudent))) return;
    if (!window.confirm("Are you sure you want to revoke Sifu status?")) return;
    try {
      const response = await axios.post(`${API}/students/${selectedStudent.id}/revoke-sifu`);
      toast.success("Sifu status revoked");
      setSelectedStudent(response.data.student);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke Sifu status");
    }
  };

  // Handle multi-class selection change
  const handleClassTypesChange = (newClassTypes) => {
    const hasAdult = newClassTypes.includes("Adults Kung Fu");
    const hasTaiChi = newClassTypes.includes("Tai Chi");
    // Calculate monthly fee based on highest fee class
    const fee = hasAdult ? DEFAULT_FEES["Adults Kung Fu"] : DEFAULT_FEES["Tigers Kung Fu"];
    
    setFormData({
      ...formData,
      class_types: newClassTypes,
      class_type: newClassTypes[0] || "",  // Primary class = first selected
      monthly_fee: fee,
      // Clear adult fields if no adult class selected
      adult_pillar: hasAdult ? formData.adult_pillar : "",
      adult_level: hasAdult ? formData.adult_level : "",
      is_sifu: hasAdult ? formData.is_sifu : false,
      // Clear tai chi level if no tai chi class selected
      tai_chi_level: hasTaiChi ? formData.tai_chi_level : "",
    });
  };

  const filteredStudents = students.filter((student) => {
    const matchesSearch = student.name.toLowerCase().includes(search.toLowerCase());
    // For multi-class support, check if student belongs to the filtered class
    const studentClasses = getClassTypesArray(student);
    const matchesClass = filterClass === "all" || studentClasses.includes(filterClass);
    return matchesSearch && matchesClass;
  });

  return (
    <div data-testid="students-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Loong Fu Tong Students 龍虎堂學生
          </h1>
          <p className="text-[#A1A1AA] mt-1">Manage your students</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Bulk Paste Button */}
          <Button 
            data-testid="bulk-paste-btn"
            onClick={() => setShowBulkPasteDialog(true)}
            variant="outline"
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-semibold uppercase tracking-wider rounded-sm" 
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <ClipboardPaste className="w-4 h-4 mr-2" />
            Bulk Paste
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setFormData(emptyStudent);
            setIsEditing(false);
            setSelectedStudent(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-student-btn" className="bg-[#D92323] hover:bg-[#b91c1c] text-white font-semibold uppercase tracking-wider rounded-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {isEditing ? "Edit Student" : "Add New Student"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Full Name *</Label>
                  <Input data-testid="student-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Enter full name" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Gender *</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      {GENDERS.map((g) => (<SelectItem key={g} value={g} className="text-white hover:bg-white/10">{g}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Date of Birth * (DD/MM/YYYY)</Label>
                  <Input data-testid="student-dob-input" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="01/01/2000" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Join Date (YYYY-MM-DD)</Label>
                  <Input data-testid="student-join-date-input" type="date" value={formData.join_date || ""} onChange={(e) => setFormData({ ...formData, join_date: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Contact Phone * (UK Format)</Label>
                  <Input value={formData.contact} onChange={(e) => setFormData({ ...formData, contact: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="07700 000000" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Email Address</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="email@example.com" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[#A1A1AA]">Classes * (Select one or more)</Label>
                  <MultiSelect
                    options={CLASS_TYPE_OPTIONS}
                    value={formData.class_types}
                    onChange={handleClassTypesChange}
                    placeholder="Select classes..."
                  />
                  <p className="text-xs text-[#A1A1AA] mt-1">Students can attend multiple classes</p>
                </div>

                {/* KIDS BELT RANK - Only show if student has a kids class */}
                {isKidsClass(formData.class_types) && (
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Kids Belt Rank</Label>
                    <Select value={formData.belt_rank} onValueChange={(v) => setFormData({ ...formData, belt_rank: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select belt" /></SelectTrigger>
                      <SelectContent className="bg-[#27272a] border-white/10">
                        {BELT_RANKS.map((belt) => (<SelectItem key={belt} value={belt} className="text-white hover:bg-white/10">{belt}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Monthly Fee (£)</Label>
                  <Input type="number" value={formData.monthly_fee} onChange={(e) => setFormData({ ...formData, monthly_fee: parseFloat(e.target.value) })} className="bg-white/5 border-white/10 text-white" placeholder="45.00" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Billing Type</Label>
                  <Select value={formData.billing_type || "standard"} onValueChange={(v) => setFormData({ ...formData, billing_type: v })}>
                    <SelectTrigger data-testid="billing-type-select" className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10">
                      <SelectItem value="standard" className="text-white hover:bg-white/10">Standard</SelectItem>
                      <SelectItem value="exempt" className="text-white hover:bg-white/10">Exempt</SelectItem>
                      <SelectItem value="pay_as_you_go" className="text-white hover:bg-white/10">Pay-As-You-Go</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Membership Expiry (DD/MM/YYYY)</Label>
                  <Input value={formData.membership_expiry || ""} onChange={(e) => setFormData({ ...formData, membership_expiry: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="01/01/2027" />
                </div>
              </div>

              {/* ==============================================
                  GRADING & RANKS SECTION
                  Shows relevant rank dropdowns based on enrolled classes
                  ============================================== */}
              {(isAdultClass(formData.class_types) || isTaiChiClass(formData.class_types)) && (
                <div className="border-t border-[#D4AF37]/30 pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    <Award className="w-5 h-5" />
                    Grading & Ranks
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* v11.9.6: UNIFIED Adults Hung Kuen Level Dropdown */}
                    {isAdultClass(formData.class_types) && (
                      <>
                        <div className="space-y-2 sm:col-span-2">
                          <Label className="text-[#A1A1AA]">Adults Hung Kuen Level</Label>
                          <select
                            data-testid="unified-hk-level-select"
                            value={formData.adult_level || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              const pillar = derivePillarFromHkLevel(val);
                              setFormData({ ...formData, adult_level: val, adult_pillar: pillar });
                            }}
                            className="w-full h-10 px-3 bg-[#27272a] border border-white/10 text-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
                          >
                            <option value="">Not set</option>
                            {/* Entry Level — standalone */}
                            <option value={UNIFIED_HK_LEVELS[0].value}>{UNIFIED_HK_LEVELS[0].value}</option>
                            {/* Beginner Stage */}
                            <optgroup label="Beginner Stage — 初級 (Cho Kup)">
                              {UNIFIED_HK_LEVELS.filter(l => l.id >= 1 && l.id <= 3).map(l => (
                                <option key={l.id} value={l.value}>{l.value}</option>
                              ))}
                            </optgroup>
                            {/* Intermediate Stage */}
                            <optgroup label="Intermediate Stage — 中級 (Jung Kup)">
                              {UNIFIED_HK_LEVELS.filter(l => l.id >= 4 && l.id <= 6).map(l => (
                                <option key={l.id} value={l.value}>{l.value}</option>
                              ))}
                            </optgroup>
                            {/* Advanced Stage */}
                            <optgroup label="Advanced Stage — 高級 (Gou Kup)">
                              {UNIFIED_HK_LEVELS.filter(l => l.id >= 7 && l.id <= 9).map(l => (
                                <option key={l.id} value={l.value}>{l.value}</option>
                              ))}
                            </optgroup>
                            {/* Instructor Stage */}
                            <optgroup label="Instructor Stage — 教練級 (Gaau Lin Kap)">
                              {UNIFIED_HK_LEVELS.filter(l => l.id >= 10 && l.id <= 12).map(l => (
                                <option key={l.id} value={l.value}>{l.value}</option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                        {/* Sifu Status Info */}
                        <div className="space-y-2 sm:col-span-2">
                          <div className="flex items-center gap-3 bg-[#27272a] p-4 rounded-sm border border-[#D4AF37]/20">
                            <Award className="w-6 h-6 text-[#D4AF37]" />
                            <div>
                              <p className="text-white font-medium">Sifu Status 師父</p>
                              <p className="text-[#A1A1AA] text-sm">
                                {isEditing && formData.is_sifu 
                                  ? "Currently has Sifu status. Use profile view to revoke."
                                  : "Must be granted manually via the student profile view after saving."}
                              </p>
                              {isEditing && formData.is_sifu && (
                                <span className="inline-flex mt-2 px-2 py-0.5 text-xs rounded-sm bg-[#D4AF37] text-black font-bold">師父 SIFU</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Tai Chi Gate/Level - only if enrolled */}
                    {isTaiChiClass(formData.class_types) && (
                      <div className="space-y-2 sm:col-span-2">
                        <Label className="text-[#A1A1AA]">Tai Chi Gate/Level</Label>
                        <Select value={formData.tai_chi_level || "none"} onValueChange={(v) => setFormData({ ...formData, tai_chi_level: v === "none" ? "" : v })}>
                          <SelectTrigger data-testid="tai-chi-level-select" className="bg-white/5 border-white/10 text-white">
                            <SelectValue placeholder="Select Tai Chi level" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#27272a] border-white/10 max-h-60">
                            <SelectItem value="none" className="text-white hover:bg-white/10">Not set</SelectItem>
                            {TAI_CHI_LEVELS.map((lvl) => (
                              <SelectItem key={lvl.value} value={lvl.value} className="text-white hover:bg-white/10">{lvl.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Medical Notes</Label>
                <Textarea value={formData.medical_notes} onChange={(e) => setFormData({ ...formData, medical_notes: e.target.value })} className="bg-white/5 border-white/10 text-white min-h-[80px]" placeholder="Any medical conditions or allergies..." />
              </div>

              {/* v3.4: Location/Timezone for Private Only & International students */}
              {(formData.class_types?.includes("Private Lessons") || 
                formData.class_types?.includes("Remote/International") ||
                formData.class_type === "Private Lessons" ||
                formData.class_type === "Remote/International") && (
                <div className="space-y-2 border border-blue-500/30 rounded-sm p-3 bg-blue-500/5">
                  <Label className="text-blue-400 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location / Timezone
                  </Label>
                  <Input 
                    value={formData.location_timezone || ""} 
                    onChange={(e) => setFormData({ ...formData, location_timezone: e.target.value })} 
                    className="bg-white/5 border-white/10 text-white" 
                    placeholder="e.g., Hong Kong (GMT+8), New York (EST), London (GMT)"
                    data-testid="location-timezone-input"
                  />
                </div>
              )}

              {/* Sifu Notes - Available for ALL students */}
              <div className="space-y-2 border-t border-[#D4AF37]/30 pt-4">
                <Label className="text-[#D4AF37] flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Sifu Notes (Instructor Observations)
                </Label>
                <Textarea 
                  value={formData.sifu_notes} 
                  onChange={(e) => setFormData({ ...formData, sifu_notes: e.target.value })} 
                  className="bg-white/5 border-[#D4AF37]/30 text-white min-h-[100px]" 
                  placeholder="Add instructor observations, training notes, progress comments..."
                  data-testid="sifu-notes-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                <Button type="submit" data-testid="save-student-btn" className="bg-[#D92323] hover:bg-[#b91c1c] text-white font-semibold uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {isEditing ? "Update" : "Add"} Student
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#18181b] border-white/10 rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input data-testid="search-students-input" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white" placeholder="Search students..." />
            </div>
            <Select value={filterClass} onValueChange={setFilterClass}>
              <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Filter by class" /></SelectTrigger>
              <SelectContent className="bg-[#27272a] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">All Classes</SelectItem>
                {CLASS_TYPES.map((c) => (<SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/10">{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Bar - appears when students are selected */}
      {selectedStudentIds.size > 0 && (
        <div className="bg-[#27272a] border border-[#D4AF37]/30 rounded-sm p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={selectedStudentIds.size === filteredStudents.length && filteredStudents.length > 0}
              onCheckedChange={handleSelectAll}
              className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
            />
            <span className="text-white font-medium">
              {selectedStudentIds.size} student{selectedStudentIds.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="mark-grading-candidates-btn"
              onClick={handleMarkGradingCandidates}
              disabled={markingCandidates}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold uppercase tracking-wider"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              <Award className="w-4 h-4 mr-2" />
              {markingCandidates ? "Tagging..." : "Mark as Grading Candidates"}
            </Button>
            <Button
              data-testid="bulk-delete-btn"
              onClick={() => setShowDeleteConfirmDialog(true)}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wider"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Students Table */}
      <Card className="bg-[#18181b] border-white/10 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectedStudentIds.size === filteredStudents.length && filteredStudents.length > 0}
                      onCheckedChange={handleSelectAll}
                      className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Classes</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Rank/Level</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#A1A1AA]">Loading...</TableCell></TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#A1A1AA]">No students found</TableCell></TableRow>
                ) : (
                  filteredStudents.map((student) => {
                    const currentForm = getFormById(student.current_form_id);
                    const studentClasses = getClassTypesArray(student);
                    const isAdult = isAdultClass(studentClasses);
                    const isSelected = selectedStudentIds.has(student.id);
                    return (
                      <TableRow 
                        key={student.id} 
                        className={`border-white/5 hover:bg-white/5 ${isSelected ? 'bg-[#D4AF37]/10' : ''}`} 
                        data-testid={`student-row-${student.id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelectStudent(student.id, checked)}
                            className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                            data-testid={`select-student-${student.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-2">
                            <span style={{ fontSize: '1.1rem' }}>
                              {student.name}
                              {calculateAge(student.date_of_birth) !== null && (
                                <span className="text-[#CCCCCC] font-normal ml-1">– Age: {calculateAge(student.date_of_birth)}</span>
                              )}
                            </span>
                            {/* v12.6.0: Grading Candidate indicator */}
                            {gradingCandidateIds.has(student.id) && (
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <span data-testid={`grading-tag-${student.id}`} className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-purple-600 text-white text-[10px] font-bold leading-none">G</span>
                              </TooltipTrigger><TooltipContent className="bg-[#27272a] border-purple-500/30"><p className="text-purple-300 text-xs">Grading Candidate</p></TooltipContent></Tooltip></TooltipProvider>
                            )}
                            {/* Billing type icons */}
                            {student.billing_type === "exempt" && (
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                              </TooltipTrigger><TooltipContent className="bg-[#27272a] border-[#D4AF37]/30"><p className="text-[#D4AF37] text-xs">Exempt</p></TooltipContent></Tooltip></TooltipProvider>
                            )}
                            {student.billing_type === "pay_as_you_go" && (
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Coins className="w-4 h-4 text-[#14b8a6]" />
                              </TooltipTrigger><TooltipContent className="bg-[#27272a] border-teal-500/30"><p className="text-teal-400 text-xs">Pay-As-You-Go</p></TooltipContent></Tooltip></TooltipProvider>
                            )}
                            {/* Sifu Notes indicator */}
                            {student.sifu_notes && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-pointer" data-testid={`sifu-notes-indicator-${student.id}`}>
                                      <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-[#27272a] border-[#D4AF37]/30 max-w-xs">
                                    <p className="text-[#D4AF37] text-xs font-semibold mb-1">Sifu Notes:</p>
                                    <p className="text-white text-sm whitespace-pre-wrap">{student.sifu_notes.length > 150 ? student.sifu_notes.substring(0, 150) + "..." : student.sifu_notes}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {/* Show Sifu badge for adults with is_sifu=true */}
                            {isAdult && student.is_sifu && (
                              <span className="inline-flex px-2 py-0.5 text-xs rounded-sm bg-[#D4AF37] text-black font-bold">師父 SIFU</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* Show multiple classes as tags */}
                          <div className="flex flex-wrap gap-1">
                            {studentClasses.map((classType, idx) => (
                              <span key={idx} className={`inline-flex px-2 py-1 text-xs rounded-sm ${getClassColor(classType)}`}>
                                {classType.replace(" Kung Fu", "")}
                              </span>
                            ))}
                            {studentClasses.length === 0 && (
                              <span className="text-[#A1A1AA]">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* Kids: Show belt rank */}
                          {!isAdult && !isTaiChiClass(studentClasses) && (
                            <span className={`inline-flex px-2 py-1 text-sm rounded-sm ${getBeltColor(student.belt_rank)}`}>
                              {student.belt_rank}
                            </span>
                          )}
                          {/* Kids + Tai Chi or just Kids with adult */}
                          {isKidsClass(studentClasses) && (isAdult || isTaiChiClass(studentClasses)) && (
                            <div className="space-y-1">
                              <span className={`inline-flex px-2 py-1 text-sm rounded-sm ${getBeltColor(student.belt_rank)}`}>
                                {student.belt_rank}
                              </span>
                            </div>
                          )}
                          {/* Adults: Show unified HK level in gold box */}
                          {isAdult && (
                            <div className="space-y-1">
                              {student.adult_level ? (
                                <span
                                  data-testid={`hk-level-${student.id}`}
                                  className="inline-flex px-[10px] py-1 rounded-[4px] border border-[#D4AF37] text-[#D4AF37] bg-transparent"
                                  style={{ fontSize: '0.9rem' }}
                                >
                                  {student.adult_level}
                                </span>
                              ) : (
                                <span className="text-[#A1A1AA] text-xs">Not set</span>
                              )}
                            </div>
                          )}
                          {/* Tai Chi: Show tai chi level */}
                          {isTaiChiClass(studentClasses) && student.tai_chi_level && (
                            <div className="space-y-1">
                              <span className="inline-flex px-2 py-1 text-xs rounded-sm bg-teal-500/20 text-teal-300 border border-teal-500/30">
                                {student.tai_chi_level}
                              </span>
                            </div>
                          )}
                          {/* No rank at all */}
                          {!isKidsClass(studentClasses) && !isAdult && !isTaiChiClass(studentClasses) && (
                            <span className="text-[#A1A1AA]">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleView(student)} data-testid={`view-student-${student.id}`} className="h-8 w-8 text-[#A1A1AA] hover:text-white hover:bg-white/10">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(student)} data-testid={`edit-student-${student.id}`} className="h-8 w-8 text-[#A1A1AA] hover:text-white hover:bg-white/10">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(student.id, student.name)} data-testid={`delete-student-${student.id}`} className="h-8 w-8 text-[#ef4444] hover:text-[#ef4444] hover:bg-red-500/10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Student Profile Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide flex items-center gap-3 flex-wrap" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <span>
                {selectedStudent?.name}
                {selectedStudent && calculateAge(selectedStudent.date_of_birth) !== null && (
                  <span className="text-[#A1A1AA] font-normal text-base ml-2">– Age: {calculateAge(selectedStudent.date_of_birth)}</span>
                )}
              </span>
              {/* Kids belt badge */}
              {selectedStudent && !isAdultClass(getClassTypesArray(selectedStudent)) && (
                <span className={`inline-flex px-2 py-1 text-xs rounded-sm ${getBeltColor(selectedStudent.belt_rank)}`}>
                  {selectedStudent.belt_rank}
                </span>
              )}
              {/* Adult Sifu badge */}
              {selectedStudent && isAdultClass(getClassTypesArray(selectedStudent)) && selectedStudent.is_sifu && (
                <span className="inline-flex px-2 py-1 text-xs rounded-sm bg-[#D4AF37] text-black font-bold">師父 SIFU</span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedStudent && (
            <Tabs value={profileTab} onValueChange={setProfileTab} className="mt-4">
              <TabsList className="bg-[#27272a] border border-white/10 p-1 mb-4">
                <TabsTrigger value="profile" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA]">
                  <Eye className="w-4 h-4 mr-2" />
                  Profile
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-[#A1A1AA]">
                  <History className="w-4 h-4 mr-2" />
                  Training History
                </TabsTrigger>
              </TabsList>

              {/* Profile Tab */}
              <TabsContent value="profile" className="space-y-6">
              
              {/* Enrolled Classes Section */}
              <div className="flex flex-wrap gap-2">
                <span className="text-[#A1A1AA] text-xs uppercase mr-2">Classes:</span>
                {getClassTypesArray(selectedStudent).map((classType, idx) => (
                  <span key={idx} className={`inline-flex px-2 py-1 text-xs rounded-sm ${getClassColor(classType)}`}>
                    {classType}
                  </span>
                ))}
              </div>

              {/* v12.5.0: Insurance Watchdog Badge — synced with payments table */}
              {(() => {
                // Skip for Private Lessons / Remote/International
                const classes = getClassTypesArray(selectedStudent);
                if (classes.includes("Private Lessons") || classes.includes("Remote/International")) return null;
                if (selectedStudent.class_type === "Private Lessons" || selectedStudent.class_type === "Remote/International") return null;
                
                // Use dynamic membershipStatus from payments table, fall back to old static method
                const insurance = membershipStatus || getInsuranceStatus(selectedStudent);
                if (!insurance) return null;
                return (
                  <Card data-testid="insurance-status-card" className={`rounded-sm border ${insurance.status === 'expired' ? 'border-red-500 bg-red-500/10' : insurance.status === 'expiring' ? 'border-orange-500 bg-orange-500/10' : insurance.status === 'valid' ? 'border-green-500 bg-green-500/10' : insurance.status === 'due' ? 'border-yellow-500 bg-yellow-500/10' : 'border-white/10 bg-white/5'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Shield className={`w-6 h-6 ${insurance.textColor}`} />
                          <div>
                            <p className="text-white font-semibold flex items-center gap-2">
                              Membership & Insurance
                              <span data-testid="insurance-badge" className={`px-2 py-0.5 text-xs rounded-sm ${insurance.color} text-white`}>
                                {insurance.label}
                              </span>
                            </p>
                            {insurance.expiryDate && (
                              <p className={`text-sm ${insurance.textColor}`}>
                                {insurance.status === 'expired' 
                                  ? `Expired ${Math.abs(insurance.days)} days ago` 
                                  : `Expires: ${insurance.expiryDate.toLocaleDateString('en-GB')} (${insurance.days} days)`}
                              </p>
                            )}
                            {insurance.paidDate && (
                              <p className="text-[#A1A1AA] text-xs mt-1">
                                Last paid: {insurance.paidDate.toLocaleDateString('en-GB')}
                              </p>
                            )}
                            {!insurance.paidDate && selectedStudent.membership_paid_date && (
                              <p className="text-[#A1A1AA] text-xs mt-1">
                                Last paid: {new Date(selectedStudent.membership_paid_date).toLocaleDateString('en-GB')}
                              </p>
                            )}
                            {insurance.status === 'due' && (
                              <p className="text-yellow-400 text-xs mt-1">Renewal pending — awaiting payment</p>
                            )}
                          </div>
                        </div>
                        <Button 
                          data-testid="renew-membership-btn"
                          size="sm" 
                          onClick={() => {
                            setRecordFeeData({...recordFeeData, category: "membership", amount: "35"});
                            setShowRecordFeeDialog(true);
                          }}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Renew
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* v3.7: Quick Actions - Record Fee */}
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowRecordFeeDialog(true)}
                  className="bg-[#D4AF37] hover:bg-[#b8962e] text-black"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Record Fee
                </Button>
              </div>
              
              {/* ==============================================
                  CURRENT GRADING LEVELS (All active pillars side-by-side)
                  ============================================== */}
              {(() => {
                const classes = getClassTypesArray(selectedStudent);
                const hasKids = isKidsClass(classes);
                const hasAdult = isAdultClass(classes);
                const hasTaiChi = isTaiChiClass(classes);
                if (!hasKids && !hasAdult && !hasTaiChi) return null;
                return (
                  <Card className="bg-[#27272a] border-[#D4AF37]/30 rounded-sm">
                    <CardContent className="p-4">
                      <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        <Award className="w-5 h-5" />
                        Current Grading Levels
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Kids Belt Rank */}
                        {hasKids && (
                          <div className="bg-[#18181b] p-4 rounded-sm border border-white/10" data-testid="grading-kids-belt">
                            <p className="text-[#A1A1AA] text-xs uppercase mb-2">Kids Belt Rank</p>
                            <span className={`inline-flex px-3 py-1.5 text-sm rounded-sm font-semibold ${getBeltColor(selectedStudent.belt_rank)}`}>
                              {selectedStudent.belt_rank || "Not set"}
                            </span>
                          </div>
                        )}
                        {/* Adult Kung Fu Level */}
                        {hasAdult && (
                          <div className="bg-[#18181b] p-4 rounded-sm border border-white/10" data-testid="grading-adult-kf">
                            <p className="text-[#A1A1AA] text-xs uppercase mb-2">Adults Hung Kuen Level</p>
                            {selectedStudent.adult_level ? (
                              <>
                                {selectedStudent.adult_pillar && (
                                  <span className={`inline-flex px-2 py-1 text-xs rounded-sm text-white mb-1 ${getAdultPillarColor(selectedStudent.adult_pillar)}`}>
                                    {selectedStudent.adult_pillar.split(" – ")[0]}
                                  </span>
                                )}
                                <p className="text-white text-sm mt-1">{selectedStudent.adult_level}</p>
                              </>
                            ) : (
                              <p className="text-[#A1A1AA] text-sm">Not set</p>
                            )}
                            {selectedStudent.is_sifu && (
                              <span className="inline-flex mt-2 px-2 py-0.5 text-xs rounded-sm bg-[#D4AF37] text-black font-bold">師父 SIFU</span>
                            )}
                          </div>
                        )}
                        {/* Tai Chi Gate/Level */}
                        {hasTaiChi && (
                          <div className="bg-[#18181b] p-4 rounded-sm border border-teal-500/30" data-testid="grading-tai-chi">
                            <p className="text-teal-400 text-xs uppercase mb-2">Tai Chi Gate/Level</p>
                            {selectedStudent.tai_chi_level ? (
                              <span className="inline-flex px-3 py-1.5 text-sm rounded-sm font-semibold bg-teal-500/20 text-teal-300 border border-teal-500/30">
                                {selectedStudent.tai_chi_level}
                              </span>
                            ) : (
                              <p className="text-[#A1A1AA] text-sm">Not set</p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Adult Sifu Grant/Revoke - Only for adults */}
              {isAdultClass(getClassTypesArray(selectedStudent)) && (
                <Card className="bg-[#27272a] border-[#D4AF37]/30 rounded-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Sifu Status 師父</p>
                        <p className="text-[#A1A1AA] text-sm">
                          {selectedStudent.is_sifu 
                            ? `Granted on ${selectedStudent.sifu_achieved_at || 'N/A'} by ${selectedStudent.sifu_granted_by || 'Admin'}`
                            : "Must be manually granted by instructor"}
                        </p>
                      </div>
                      {selectedStudent.is_sifu ? (
                        <Button
                          data-testid="revoke-sifu-btn"
                          onClick={handleRevokeSifu}
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        >
                          Revoke Sifu
                        </Button>
                      ) : (
                        <Button
                          data-testid="grant-sifu-btn"
                          onClick={handleGrantSifu}
                          className="bg-[#D4AF37] hover:bg-[#b8962e] text-black font-semibold"
                        >
                          Grant Sifu Status
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Student Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase">Date of Birth</p>
                  <p className="text-white">{selectedStudent.date_of_birth}</p>
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase">Gender</p>
                  <p className="text-white">{selectedStudent.gender}</p>
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase">Contact</p>
                  <p className="text-white">{selectedStudent.contact}</p>
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase">Monthly Fee</p>
                  <p className="text-white font-mono">£{selectedStudent.monthly_fee?.toFixed(2)}</p>
                </div>
              </div>
              
              {selectedStudent.medical_notes && (
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase">Medical Notes</p>
                  <p className="text-white bg-white/5 p-3 rounded-sm mt-1">{selectedStudent.medical_notes}</p>
                </div>
              )}

              {/* v3.4: Location/Timezone for Private Only & International students */}
              {selectedStudent.location_timezone && (
                <div className="border border-blue-500/30 rounded-sm p-3 bg-blue-500/5">
                  <p className="text-blue-400 text-xs uppercase flex items-center gap-2">
                    <MapPin className="w-3 h-3" />
                    Location / Timezone
                  </p>
                  <p className="text-white mt-1">{selectedStudent.location_timezone}</p>
                </div>
              )}

              {/* Sifu Notes - Display for ALL students */}
              <div className="border-t border-[#D4AF37]/30 pt-4 mt-4">
                <p className="text-[#D4AF37] text-xs uppercase flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4" />
                  Sifu Notes
                </p>
                {selectedStudent.sifu_notes ? (
                  <p className="text-white bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-3 rounded-sm whitespace-pre-wrap" data-testid="sifu-notes-display">{selectedStudent.sifu_notes}</p>
                ) : (
                  <p className="text-[#A1A1AA] italic">No instructor notes yet. Click "Edit Full Profile" to add.</p>
                )}
              </div>

              {/* Edit Button */}
              <div className="flex justify-end pt-4 border-t border-white/10">
                <Button onClick={() => { setViewDialogOpen(false); handleEdit(selectedStudent); }} className="bg-[#D92323] hover:bg-[#b91c1c] text-white font-semibold uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Full Profile
                </Button>
              </div>
              </TabsContent>

              {/* v3.4: Training History Tab */}
              <TabsContent value="history" className="space-y-4">
                <Card className="bg-[#27272a] border-white/10 rounded-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        <History className="w-5 h-5 text-[#D4AF37]" />
                        Unified Training History
                      </h3>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-blue-400" />
                          <span className="text-blue-400">{trainingHistory.filter(h => h.type === "Group").length}</span>
                          <span className="text-[#A1A1AA]">Group</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <UserCheck className="w-4 h-4 text-green-400" />
                          <span className="text-green-400">{trainingHistory.filter(h => h.type !== "Group").length}</span>
                          <span className="text-[#A1A1AA]">Private</span>
                        </span>
                      </div>
                    </div>

                    {loadingHistory ? (
                      <div className="text-center py-8 text-[#A1A1AA]">Loading training history...</div>
                    ) : trainingHistory.length === 0 ? (
                      <div className="text-center py-8 text-[#A1A1AA]">No training sessions recorded yet.</div>
                    ) : (
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-[#27272a]">
                            <tr className="border-b border-white/10">
                              <th className="text-left p-2 text-[#A1A1AA] text-xs uppercase">Date</th>
                              <th className="text-left p-2 text-[#A1A1AA] text-xs uppercase">Type</th>
                              <th className="text-left p-2 text-[#A1A1AA] text-xs uppercase">Status</th>
                              <th className="text-left p-2 text-[#A1A1AA] text-xs uppercase">Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trainingHistory.map((session, idx) => (
                              <tr key={session.id || idx} className="border-b border-white/5 hover:bg-white/5">
                                <td className="p-2 text-white">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-3 h-3 text-[#A1A1AA]" />
                                    {session.date.includes('/') ? session.date : new Date(session.date).toLocaleDateString('en-GB')}
                                  </div>
                                  {session.time && (
                                    <span className="text-[#A1A1AA] text-xs ml-5">{session.time}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {session.type === "Group" ? (
                                    <span className="flex items-center gap-1 text-blue-400">
                                      <Users className="w-3 h-3" /> Group
                                    </span>
                                  ) : session.type.includes("Online") ? (
                                    <span className="flex items-center gap-1 text-purple-400">
                                      <Video className="w-3 h-3" /> Private (Online)
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-green-400">
                                      <MapPin className="w-3 h-3" /> Private (F2F)
                                    </span>
                                  )}
                                </td>
                                <td className="p-2">
                                  <span className={`px-2 py-0.5 text-xs rounded-sm ${
                                    session.status === "Completed" || session.status === "present" ? "bg-green-500 text-white" :
                                    session.status === "Late-Cancel" ? "bg-orange-500 text-white" :
                                    session.status === "Scheduled" ? "bg-blue-500 text-white" :
                                    "bg-gray-500 text-white"
                                  }`}>
                                    {session.status === "present" ? "Present" : session.status}
                                  </span>
                                  {session.payment_status && (
                                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-sm ${
                                      session.payment_status === "Paid" ? "bg-green-600 text-white" : "bg-red-500 text-white"
                                    }`}>
                                      {session.payment_status}
                                    </span>
                                  )}
                                </td>
                                <td className="p-2 text-[#A1A1AA] text-sm max-w-xs truncate">
                                  {session.notes || "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Form Dialog */}
      <Dialog open={showAddFormDialog} onOpenChange={setShowAddFormDialog}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Add Form to {selectedStudent?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#A1A1AA] text-xs uppercase">Select Form</Label>
              <Select value={selectedFormToAdd} onValueChange={setSelectedFormToAdd}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                  <SelectValue placeholder="Choose a form to assign..." />
                </SelectTrigger>
                <SelectContent className="bg-[#27272a] border-white/10 max-h-80">
                  {selectedStudent && getAvailableForms(selectedStudent.id).map(form => (
                    <SelectItem 
                      key={form.id} 
                      value={form.id}
                      className="text-white hover:bg-white/10"
                    >
                      <div className="flex flex-col">
                        <span>{form.english_name}</span>
                        <span className="text-xs text-[#A1A1AA]">{form.cantonese} | {form.traditional_chinese}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => { setShowAddFormDialog(false); setSelectedFormToAdd(""); }}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddFormToStudent}
                disabled={!selectedFormToAdd}
                className="flex-1 bg-[#D4AF37] hover:bg-[#b8962e] text-black"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Form
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Paste Dialog */}
      <Dialog open={showBulkPasteDialog} onOpenChange={setShowBulkPasteDialog}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <ClipboardPaste className="w-5 h-5 text-[#D4AF37]" />
              Bulk Paste Students
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Paste student names (one per line)</Label>
              <Textarea
                data-testid="bulk-paste-textarea"
                value={bulkPasteText}
                onChange={(e) => setBulkPasteText(e.target.value)}
                placeholder="John Smith
Jane Doe
Michael Chen
..."
                className="bg-white/5 border-white/10 text-white min-h-[150px] font-mono"
              />
              <p className="text-xs text-[#A1A1AA]">
                {bulkPasteText.split('\n').filter(n => n.trim()).length} names detected
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Assign to Classes *</Label>
              <MultiSelect
                options={CLASS_TYPE_OPTIONS}
                value={bulkPasteClasses}
                onChange={setBulkPasteClasses}
                placeholder="Select classes for all students..."
              />
            </div>

            {/* Belt rank for kids */}
            {!bulkPasteClasses.includes("Adults Kung Fu") && bulkPasteClasses.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Belt Rank (Kids)</Label>
                <Select value={bulkPasteBelt} onValueChange={setBulkPasteBelt}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select belt" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    {BELT_RANKS.map((belt) => (
                      <SelectItem key={belt} value={belt} className="text-white hover:bg-white/10">{belt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* v11.9.6: Unified Adults Hung Kuen Level */}
            {bulkPasteClasses.includes("Adults Kung Fu") && (
              <div className="space-y-4 border-t border-[#D4AF37]/30 pt-4">
                <h4 className="text-[#D4AF37] font-semibold">Adults Hung Kuen Level (Optional)</h4>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs">Hung Kuen Level</Label>
                  <select
                    data-testid="bulk-hk-level-select"
                    value={bulkPasteAdultLevel || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBulkPasteAdultLevel(val);
                      setBulkPasteAdultPillar(derivePillarFromHkLevel(val));
                    }}
                    className="w-full h-10 px-3 bg-[#27272a] border border-white/10 text-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
                  >
                    <option value="">Not set</option>
                    <option value={UNIFIED_HK_LEVELS[0].value}>{UNIFIED_HK_LEVELS[0].value}</option>
                    <optgroup label="Beginner Stage — 初級 (Cho Kup)">
                      {UNIFIED_HK_LEVELS.filter(l => l.id >= 1 && l.id <= 3).map(l => (
                        <option key={l.id} value={l.value}>{l.value}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Intermediate Stage — 中級 (Jung Kup)">
                      {UNIFIED_HK_LEVELS.filter(l => l.id >= 4 && l.id <= 6).map(l => (
                        <option key={l.id} value={l.value}>{l.value}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Advanced Stage — 高級 (Gou Kup)">
                      {UNIFIED_HK_LEVELS.filter(l => l.id >= 7 && l.id <= 9).map(l => (
                        <option key={l.id} value={l.value}>{l.value}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Instructor Stage — 教練級 (Gaau Lin Kap)">
                      {UNIFIED_HK_LEVELS.filter(l => l.id >= 10 && l.id <= 12).map(l => (
                        <option key={l.id} value={l.value}>{l.value}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>
            )}

            <div className="bg-[#27272a] p-3 rounded-sm text-sm text-[#A1A1AA]">
              <p><strong className="text-white">Note:</strong> All imported students will have placeholder contact details (TBC) that you can update later.</p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => {
                  setShowBulkPasteDialog(false);
                  setBulkPasteText("");
                  setBulkPasteClasses([]);
                }}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                data-testid="bulk-import-btn"
                onClick={handleBulkImport}
                disabled={bulkImporting || !bulkPasteText.trim() || bulkPasteClasses.length === 0}
                className="flex-1 bg-[#D4AF37] hover:bg-[#b8962e] text-black font-semibold"
              >
                {bulkImporting ? (
                  "Importing..."
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {bulkPasteText.split('\n').filter(n => n.trim()).length} Students
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide flex items-center gap-2 text-red-500" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <AlertTriangle className="w-6 h-6" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
              <p className="text-white text-center">
                Are you sure you want to delete <span className="font-bold text-red-400">{selectedStudentIds.size}</span> student{selectedStudentIds.size !== 1 ? 's' : ''}?
              </p>
              <p className="text-[#A1A1AA] text-sm text-center mt-2">
                All attendance records, payments, and form assignments for these students will also be deleted.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowDeleteConfirmDialog(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                disabled={bulkDeleting}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-bulk-delete-btn"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                {bulkDeleting ? (
                  "Deleting..."
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Yes, Delete {selectedStudentIds.size} Student{selectedStudentIds.size !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={!!singleDeleteStudent} onOpenChange={(open) => !open && setSingleDeleteStudent(null)}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide flex items-center gap-2 text-red-500" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <AlertTriangle className="w-6 h-6" />
              Delete Student
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
              <p className="text-white text-center">
                Are you sure you want to delete <span className="font-bold text-red-400">{singleDeleteStudent?.name}</span>?
              </p>
              <p className="text-[#A1A1AA] text-sm text-center mt-2">
                All attendance records, payments, and form assignments will also be deleted.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setSingleDeleteStudent(null)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-single-delete-btn"
                onClick={confirmSingleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Yes, Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* v3.7: Record Fee Dialog */}
      <Dialog open={showRecordFeeDialog} onOpenChange={setShowRecordFeeDialog}>
        <DialogContent className="bg-[#18181b] border-[#D4AF37]/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <DollarSign className="w-5 h-5" />
              Record Fee Payment
            </DialogTitle>
            {selectedStudent && (
              <DialogDescription className="text-[#A1A1AA]">
                Recording payment for: {selectedStudent.name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Payment Category */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Payment Category</Label>
              <Select value={recordFeeData.category} onValueChange={(v) => setRecordFeeData({...recordFeeData, category: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#27272a] border-white/10">
                  {PAYMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/10">
                      <span className={`inline-block w-3 h-3 rounded-full mr-2 ${cat.color}`}></span>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Amount (£)</Label>
              <Input 
                type="number"
                value={recordFeeData.amount}
                onChange={(e) => setRecordFeeData({...recordFeeData, amount: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Enter amount"
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Payment Method</Label>
              <Select value={recordFeeData.payment_method} onValueChange={(v) => setRecordFeeData({...recordFeeData, payment_method: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#27272a] border-white/10">
                  <SelectItem value="Cash" className="text-white hover:bg-white/10">Cash</SelectItem>
                  <SelectItem value="Bank Transfer" className="text-white hover:bg-white/10">Bank Transfer</SelectItem>
                  <SelectItem value="Card" className="text-white hover:bg-white/10">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Payment Date</Label>
              <Input 
                type="date"
                value={recordFeeData.date}
                onChange={(e) => setRecordFeeData({...recordFeeData, date: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Notes (optional)</Label>
              <Textarea 
                value={recordFeeData.notes}
                onChange={(e) => setRecordFeeData({...recordFeeData, notes: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Any additional notes..."
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={() => setShowRecordFeeDialog(false)} 
                variant="outline" 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRecordFee}
                disabled={recordingFee || !recordFeeData.amount}
                className="flex-1 bg-[#D4AF37] hover:bg-[#b8962e] text-black"
              >
                {recordingFee ? "Recording..." : "Record Payment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
